import Avatar from '@components/avatar'
import PropTypes from 'prop-types'
import classnames from 'classnames'

/**
 * LIGHT_AVATAR_THEMES
 * Outreach-tag style palette: soft light pastel background colors paired with dark text colors.
 */
export const LIGHT_AVATAR_THEMES = [
    { name: 'light-slate',  bg: '#f1f5f9', color: '#334155' },
    { name: 'light-blue',   bg: '#e3f2fd', color: '#1d57a4' },
    { name: 'light-green',  bg: '#e6f5ee', color: '#0d7d59' },
    { name: 'light-amber',  bg: '#fff8e1', color: '#92400e' },
    { name: 'light-rose',   bg: '#fce4ec', color: '#9f1239' },
    { name: 'light-teal',   bg: '#e0f2f1', color: '#00695c' },
    { name: 'light-orange', bg: '#fff3e0', color: '#c2410c' },
    { name: 'light-indigo', bg: '#e8eaf6', color: '#283593' },
    { name: 'light-cyan',   bg: '#e0f7fa', color: '#006064' },
    { name: 'light-pink',   bg: '#fdf2f8', color: '#be185d' }
]

/**
 * CustomAvatar
 *
 * Renders the user/brand profile image if available.
 * If missing or default placeholder, renders a stylized tag-like avatar:
 * a light pastel background badge displaying uppercase initial letters in a matching dark text color.
 */
export const getAvatarProps = (name, imgUrl) => {
    const hasImage = imgUrl &&
        typeof imgUrl === 'string' &&
        imgUrl.trim() !== '' &&
        !imgUrl.includes('avatar-blank') &&
        !imgUrl.includes('Blank-logo') &&
        !imgUrl.includes('blank') &&
        !imgUrl.includes('null') &&
        !imgUrl.includes('undefined')

    if (hasImage) {
        return { img: imgUrl }
    }

    const rawStr = (name || 'User').trim()

    // Strip out all non-alphabetic characters except spaces (e.g. "U.S. Soy" -> "US Soy")
    const cleanStr = rawStr
        .replace(/[^a-zA-Z\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim()

    const strToUse = cleanStr.length > 0 ? cleanStr : 'User'

    let hash = 0
    for (let i = 0; i < rawStr.length; i++) {
        hash = rawStr.charCodeAt(i) + ((hash << 5) - hash)
    }
    const themeIndex = Math.abs(hash) % LIGHT_AVATAR_THEMES.length
    const theme = LIGHT_AVATAR_THEMES[themeIndex]

    const words = strToUse.split(/\s+/)
    let initials = ''
    if (words.length >= 2) {
        initials = (words[0][0] + words[1][0]).toUpperCase()
    } else if (words.length === 1 && words[0].length >= 2) {
        initials = words[0].substring(0, 2).toUpperCase()
    } else if (words.length === 1 && words[0].length === 1) {
        initials = words[0][0].toUpperCase()
    } else {
        initials = 'U'
    }

    return {
        color: theme.name,
        content: initials,
        initials: false,
        img: false,
        contentStyles: {
            backgroundColor: theme.bg,
            color: `${theme.color} !important`,
            fontWeight: '700',
            fontSize: '0.9rem'
        },
        containerStyle: {
            backgroundColor: theme.bg
        }
    }
}

const CustomAvatar = props => {
    const { name, img, size, className, imgClassName, imgHeight, imgWidth, style, ...rest } = props
    const avatarProps = getAvatarProps(name, img)

    const targetSize = parseInt(imgHeight || imgWidth || 42, 10) || 42

    const combinedStyle = {
        width: `${targetSize}px`,
        height: `${targetSize}px`,
        minWidth: `${targetSize}px`,
        minHeight: `${targetSize}px`,
        borderRadius: '50%',
        boxShadow: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...avatarProps.containerStyle,
        ...style
    }

    return (
        <Avatar
            {...avatarProps}
            size={size}
            className={classnames('msg-avatar', className)}
            imgClassName={imgClassName}
            imgHeight={targetSize}
            imgWidth={targetSize}
            style={combinedStyle}
            {...rest}
        />
    )
}

CustomAvatar.propTypes = {
    name: PropTypes.string,
    img: PropTypes.string,
    size: PropTypes.string,
    className: PropTypes.string,
    imgClassName: PropTypes.string,
    imgHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    imgWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default CustomAvatar
